/* ==========================================================================
   Syrvis — facets.js (collection pages only, vanilla, no dependencies)
   Instant filtering: listens to the filter/sort forms, fetches the filtered
   collection through the Section Rendering API, swaps the results and filter
   panels in place and keeps the URL in sync with pushState.
   Loaded by sections/main-collection.liquid after theme.js/motion.js.
   ========================================================================== */
(() => {
  'use strict';

  // Swapped on every change: toolbar (count + active chips), sidebar filters,
  // product grid and pagination all live inside this wrapper in
  // sections/main-collection.liquid.
  const RESULTS_SELECTOR = '[data-collection-root]';
  // The product grid itself — <ul class="product-grid"> in main-collection.liquid.
  const PRODUCT_GRID_SELECTOR = '.product-grid';
  // Mobile filter drawer body (outside the results wrapper, swapped separately).
  const DRAWER_BODY_SELECTOR = '[data-facets-drawer-body]';
  const FORM_SELECTOR = 'form[data-facets-form]';

  const PRICE_DEBOUNCE_MS = 500;  // typing a price shouldn't fire a request per keystroke
  const SKELETON_DELAY_MS = 500;  // only show skeleton cards if the response is slow

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const SK = window.SK || {};

  const root = () => $(RESULTS_SELECTOR);
  if (!root()) return;

  const sectionId = root().dataset.sectionId;
  const cache = new Map();
  let controller = null;
  let priceTimer = null;
  let lastQuery = window.location.search.slice(1);

  /* ---- Build the query string from the forms ---------------------------- */
  function queryFrom(form) {
    // Filters come from whichever panel changed (sidebar or drawer — both are
    // re-rendered from the response, so they never drift apart). Sort always
    // comes from the toolbar select.
    const filters = form.dataset.facetsForm === 'filters' ? form : $('form[data-facets-form="filters"]');
    const params = new URLSearchParams();
    if (filters) {
      new FormData(filters).forEach((value, key) => {
        if (value !== '' && key !== 'sort_by') params.append(key, value);
      });
    }
    const sort = $('form[data-facets-form="sort"] select[name="sort_by"]');
    if (sort) params.set('sort_by', sort.value);
    return params.toString();
  }

  /* ---- Keep UI state the server can't know about ------------------------ */
  // Which groups the shopper opened/closed, per panel, keyed by filter param.
  function snapshotOpenState() {
    const state = {};
    $$('.facets').forEach((form) => {
      state[form.id] = {};
      $$('[data-facet-group]', form).forEach((d) => { state[form.id][d.dataset.facetGroup] = d.open; });
    });
    return state;
  }
  function restoreOpenState(state) {
    Object.entries(state).forEach(([formId, groups]) => {
      const form = document.getElementById(formId);
      if (!form) return;
      $$('[data-facet-group]', form).forEach((d) => {
        if (d.dataset.facetGroup in groups) d.open = groups[d.dataset.facetGroup];
      });
    });
  }

  /* ---- Fetch + swap ------------------------------------------------------ */
  async function fetchSection(query, signal) {
    if (cache.has(query)) return cache.get(query);
    const sep = query ? '&' : '';
    const res = await fetch(`${window.location.pathname}?${query}${sep}section_id=${sectionId}`, { signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const html = await res.text();
    cache.set(query, html);
    return html;
  }

  async function render(query, { push = true } = {}) {
    controller?.abort();                       // newest change wins
    controller = new AbortController();
    const { signal } = controller;

    const current = root();
    current.classList.add('is-loading');
    current.setAttribute('aria-busy', 'true');
    const skeletonTimer = setTimeout(() => SK.Skeleton?.grid(current), SKELETON_DELAY_MS);
    SK.Progress?.start();

    const focusedId = document.activeElement?.id;
    const openState = snapshotOpenState();

    try {
      const html = await fetchSection(query, signal);
      if (signal.aborted) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const nextResults = $(RESULTS_SELECTOR, doc);
      if (!nextResults) throw new Error('Results wrapper missing from section response');
      current.replaceWith(nextResults);

      const nextDrawer = $(DRAWER_BODY_SELECTOR, doc);
      const drawer = $(DRAWER_BODY_SELECTOR);
      if (nextDrawer && drawer) drawer.replaceWith(nextDrawer);

      restoreOpenState(openState);
      // Keep keyboard users where they were (ids are stable per panel).
      if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });

      lastQuery = query;
      if (push) history.pushState({ facets: true }, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
      SK.ImageLoad?.scan(root());

      // Fewer results can leave the shopper staring at the footer — bring the
      // top of the grid (or the empty state) back into view if it's above them.
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 56;
      const target = $(PRODUCT_GRID_SELECTOR, root()) || root();
      if (target.getBoundingClientRect().top < header) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: window.scrollY + target.getBoundingClientRect().top - header - 80, behavior: reduce ? 'auto' : 'smooth' });
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      // Anything unexpected: fall back to a normal page load with the same filters.
      window.location.search = query;
    } finally {
      clearTimeout(skeletonTimer);
      if (!signal.aborted) {
        root()?.classList.remove('is-loading');
        root()?.removeAttribute('aria-busy');
        SK.Progress?.done();
      }
    }
  }

  /* ---- Events (delegated, so they survive every swap) -------------------- */
  document.addEventListener('change', (e) => {
    const form = e.target.closest(FORM_SELECTOR);
    if (!form) return;
    clearTimeout(priceTimer);
    render(queryFrom(form));                   // checkboxes + sort apply instantly
  });

  document.addEventListener('input', (e) => {
    const form = e.target.closest(FORM_SELECTOR);
    if (!form || e.target.type !== 'number') return;
    clearTimeout(priceTimer);
    priceTimer = setTimeout(() => render(queryFrom(form)), PRICE_DEBOUNCE_MS);
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest(FORM_SELECTOR);
    if (!form) return;
    e.preventDefault();                        // Enter in a price field
    clearTimeout(priceTimer);
    render(queryFrom(form));
  });

  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-facet-link]');
    if (!link) return;
    e.preventDefault();                        // active-filter chips + clear all
    render(new URL(link.href, window.location.origin).search.slice(1));
  });

  window.addEventListener('popstate', () => {
    const query = window.location.search.slice(1);
    if (query !== lastQuery) render(query, { push: false });   // ignore #hash-only changes
  });

})();
